import { useFieldArray, useForm, UseFormReturn } from "react-hook-form";
import { useEffect, useMemo } from "react";
import { useSnackbar } from "react-simple-snackbar";
import { useQueryClient } from "react-query";
import { useSocket } from "$utils/socket";
import {
  snackbarErrorOptions,
  snackbarSuccessOptions,
} from "$utils/SnackbarOptions";
import { Reception, Suborder } from "$types";
import useExport from "$hooks/useExport";
import useAuth from "$hooks/useAuth";
import ReferenceType from "$types/ReferenceType";
import ReferencingType from "$types/ReferencingType";
import ExportTypes from "$types/ExportTypes";
import { ExportOrderReferenceType } from "$types/ExportOrderReferenceType";
import { FieldValues } from "$hooks/useReferencingForm";
import { downloadFile } from "$components/Export/utils";
import { deleteProduct } from "$api/productApi";
import { ProductService } from "$services";
import { ProductReferenceList } from "$types/ProductReferenceList";
import { OrderReference as OrderReferenceType } from "$types/OrderReference";
import { FormValues } from "../context/ReferencingFormContext";
import { useDefaultValueReferencing } from "../../useDefaultValueReferencing";
import { CacheInvalidationAction } from "$utils/cache";
import { getProductQuantityData } from "$utils/orders";

type UseReferencingFormLogicProps = {
  reception: Reception;
  onSubmit: (data: any) => Promise<any>;
  togglePopin: (open: boolean) => void;
  resetEditedBlocks: () => void;
  pendingDeletions: string[];
  setIsSavingProducts: (saving: boolean) => void;
  hasReferencingCategory: boolean;
  referencingCategory: any;
  getDefaultOrderReference: (category: any, suborders: any[]) => any;
  getDefaultProductReference: (suborders: any[], data: any) => any;
  isDeletable: (
    reference: string,
    companyId: string,
    receptionId: string | number
  ) => Promise<boolean>;
  processProductRefProducts: (
    productReferenceLists: any[],
    callback: any
  ) => Promise<void>;
  processOrderRefProducts: (
    orderReferences: any[],
    companyId: string,
    callback: any
  ) => Promise<void>;
  selectableSuborders: any[];
  setSelectableSuborders: (suborders: any[]) => void;
  editedBlocks: {
    items: { id: string; type: string }[];
    add: (item: { id: string; type: string }) => void;
  };
  setToDeleteReferences: (references: string[]) => void;
  indexToDelete: number | null;
  setIndexToDelete: (index: number | null) => void;
  openCancelModal: boolean;
  setOpenCancelModal: (open: boolean) => void;
  addToEditedBlocks: (block: { id: string; type: string }) => void;
  deletePreviousOrderRef: (orderRef: OrderReferenceType | null) => void;
  deletePreviousProductListRef: (
    productRefList: ProductReferenceList | null
  ) => void;
  getPreviousProductListFromOrderRef: (
    orderRef: OrderReferenceType
  ) => ProductReferenceList;
  getPreviousOrderRefFromProductList: (
    productRefList: ProductReferenceList
  ) => OrderReferenceType;

  toDeleteOrderRef: OrderReferenceType | null;
  toDeleteProductRefList: ProductReferenceList | null;
  setToKeepReferences: (refs: string[]) => void;
  // setToKeepIdenticalReferences: (refs: string[]) => void;
  toKeepIdenticalReferences: string[];
  setToDeleteOrderRef: (ref: OrderReferenceType | null) => void;
  setToDeleteProductRefList: (ref: ProductReferenceList | null) => void;
  setOpenDeleteModal: (open: boolean) => void;
  setConfirmedDeletions: (confirmed: boolean) => void;
  toDeleteReferences: string[];
  setPendingDeletions:
    | ((prev: string[]) => string[])
    | ((deletions: string[]) => void);
};

type UseReferencingFormLogicReturn = {
  form: UseFormReturn<FormValues>;
  handleOnSubmit: (data: any, closePopin: boolean) => Promise<void>;
  handleOrderReference: (
    OrderReferenceParams: any,
    isReception: boolean
  ) => Promise<void>;
  handleCancelUpdate: () => void;
  refreshRefItem: (orderRefItem: any) => any;
  orderRefFields: any[];
  productRefListFields: any[];
  selectableOrders: any[];
  onSelectSuborders: (newSuborders: any[]) => void;
  initialReceptionReferences: any[];
  removeOrderRef: (index: number) => void;
  prependOrderRef: (orderRef: any) => void;
  removeProductRefList: (index: number) => void;
  prependProductRefList: (productRefList: any) => void;
  onDeleteProducts: () => Promise<void>;
  isLoading: boolean;
};

const normalizeOrders = (orders: any): any[] => {
  if (!orders) return [];

  // New Strapi format: orders: { data: [...] }
  if (orders.data && Array.isArray(orders.data)) {
    return orders.data.map((order: any) => ({
      id: order.id,
      ...order.attributes,
    }));
  }

  // Old format: orders: [...]
  if (Array.isArray(orders)) {
    return orders;
  }

  // Fallback
  return [];
};

const toCompanyId = (company: any): string | undefined => {
  if (!company) return undefined;
  if (typeof company === "string") return company;
  return company._id ?? company.id;
};

const resolveReferenceCompanyId = (
  orders: any[],
  receptionCompany: any
): string | undefined =>
  normalizeOrders(orders)
    .map((order) => toCompanyId(order.company))
    .find(Boolean) ?? toCompanyId(receptionCompany);

const computeReceptionReferenceQuantityFromReception = (
  productReference: any,
  receptionProductQuantity: any[]
): number => {
  // If productSuborderContexts exists, it’s the most precise mapping.
  if (
    productReference?.productSuborderContexts &&
    Array.isArray(productReference.productSuborderContexts) &&
    productReference.productSuborderContexts.length > 0
  ) {
    return productReference.productSuborderContexts.reduce(
      (sum: number, ctx: any) => {
        const found = receptionProductQuantity.find(
          (pq: any) =>
            pq.productId === productReference.originalProductReference &&
            pq.suborderReference === ctx.suborderReference &&
            pq.orderReference === ctx.orderReference
        );
        return sum + (Number(found?.receivedQuantity) || 0);
      },
      0
    );
  }

  // Fallback: sum on every suborder that contains the original product.
  const suborders = Array.isArray(productReference?.suborders)
    ? productReference.suborders
    : [];

  const subordersWithProduct = suborders.filter((suborder: any) =>
    suborder?.suborderProducts?.some(
      (p: any) => p.reference === productReference.originalProductReference
    )
  );

  return subordersWithProduct.reduce((sum: number, suborder: any) => {
    const found = receptionProductQuantity.find(
      (pq: any) =>
        pq.productId === productReference.originalProductReference &&
        pq.suborderReference === suborder.reference
    );
    return sum + (Number(found?.receivedQuantity) || 0);
  }, 0);
};

const getCoreProductIdByReference = async (
  queryClient: any,
  companyId: string,
  reference: string
): Promise<string | undefined> => {
  if (!reference) return undefined;

  // Try existing cache first
  const cacheKey = [
    CacheInvalidationAction.GET_PRODUCT_BY_REFERENCE,
    companyId,
    reference,
  ];
  const cached = queryClient.getQueryData(cacheKey) as any;
  const cachedProduct = Array.isArray(cached) ? cached?.[0] : undefined;
  if (cachedProduct?._id) return cachedProduct._id;

  // Fallback to service call
  const fetched = await ProductService.getProductByReference({
    company: companyId,
    reference,
  });
  const fetchedProduct = Array.isArray(fetched) ? fetched?.[0] : undefined;
  return fetchedProduct?._id;
};

export const useReferencingFormLogic = ({
  reception,
  onSubmit,
  togglePopin,
  resetEditedBlocks,
  setPendingDeletions,
  addToEditedBlocks,
  pendingDeletions,
  setIsSavingProducts,
  hasReferencingCategory,
  referencingCategory,
  getDefaultOrderReference,
  getDefaultProductReference,
  processProductRefProducts,
  processOrderRefProducts,
  selectableSuborders,
  setSelectableSuborders,
  setToDeleteReferences,
  indexToDelete,
  deletePreviousOrderRef,
  deletePreviousProductListRef,
  getPreviousProductListFromOrderRef,
  setOpenCancelModal,
  toDeleteOrderRef,
  toDeleteProductRefList,
  setToKeepReferences,
  // setToKeepIdenticalReferences,
  toKeepIdenticalReferences,
  setToDeleteOrderRef,
  setToDeleteProductRefList,
  setOpenDeleteModal,
  setConfirmedDeletions,
  toDeleteReferences,
  setIndexToDelete,
}: UseReferencingFormLogicProps): UseReferencingFormLogicReturn => {
  const { token } = useAuth();
  const socket = useSocket();
  const queryClient = useQueryClient();
  const { exportData } = useExport();
  const [openSnackbar] = useSnackbar(snackbarSuccessOptions);
  const [openSnackbarError] = useSnackbar(snackbarErrorOptions);

  const { defaultValues, isLoading: isLoadingDefaultValues } =
    useDefaultValueReferencing({
      companyId: reception.company,
      reception,
    });

  const form = useForm<FormValues>({
    defaultValues,
    mode: "onChange",
  });

  const {
    fields: orderRefFields,
    remove: removeOrderRef,
    prepend: prependOrderRef,
  } = useFieldArray({
    control: form.control,
    name: "orderReferences",
    keyName: "_id",
  });

  const {
    fields: productRefListFields,
    prepend: prependProductRefList,
    remove: removeProductRefList,
  } = useFieldArray({
    control: form.control,
    name: "productReferenceLists",
    keyName: "_id",
  });

  // Make sure the form is reset when defaultValues change
  useEffect(() => {
    if (defaultValues) {
      form.reset(defaultValues);
    }
  }, [defaultValues, form]);

  const onSelectSuborders = (newSuborders: Suborder[]) => {
    addToEditedBlocks({
      id: String(newSuborders[0].id),
      type: ReferenceType.ORDER,
    });
    if (hasReferencingCategory) {
      prependOrderRef({
        id: null, // check if null or newSuborders[0].id
        ...getDefaultOrderReference(referencingCategory, newSuborders),
      });
    } else {
      // If the referencing category doesn't exist, create product referencing arrays.
      // Also, all the products are referenced "à l'identique" (originalProductReference === productReference)
      const allProductReferences = newSuborders.flatMap((suborder) =>
        suborder.suborderProducts.map((product) =>
          getDefaultProductReference(newSuborders, {
            originalProductReference: product.reference,
            referenceType: ReferenceType.PRODUCT,
            suborders: newSuborders,
            productReference: product.reference,
          })
        )
      );

      const consolidatedProductRefList = {
        id: null,
        suborders: newSuborders, // All selected suborders
        productReferences: allProductReferences,
      };

      prependProductRefList(consolidatedProductRefList);
    }
  };

  const selectableOrders = useMemo(() => {
    const result = normalizeOrders(reception.orders).reduce(
      (filteredOrders, order) => {
        const hasOnlyOneSuborder = order.suborders.length === 1;
        const suborders = order.suborders.filter((suborder) =>
          selectableSuborders.map((s) => s.id).includes(suborder.id)
        );
        return suborders.length === 0
          ? filteredOrders
          : [...filteredOrders, { ...order, hasOnlyOneSuborder, suborders }];
      },
      []
    );
    return result;
  }, [reception.orders, selectableSuborders]);

  // Update selectable orders
  useEffect(() => {
    const allSuborders = normalizeOrders(reception.orders).flatMap(
      (o) => o.suborders
    );
    const newSelectable = allSuborders
      .filter((suborder) => {
        const { reference: suborderReference, suborderProducts } = suborder;
        const suborderproductQuantity = reception.productQuantity.filter(
          (pq) => pq.suborderReference === suborderReference
        );
        const inOrderRefs = !!orderRefFields.find((suborder2) =>
          suborder2?.suborders
            ?.map((s) => s.reference)
            .includes(suborder.reference)
        );
        const inProductRefs = !!productRefListFields.find((productRefList) =>
          productRefList?.suborders
            ?.map((s) => s.reference)
            .includes(suborder.reference)
        );
        const atLeastOneReceived = suborderproductQuantity?.some(
          (productQuantity) => {
            return (
              suborderProducts
                .map((p) => p.reference)
                .includes(productQuantity.productId) &&
              productQuantity.receivedQuantity > 0
            );
          }
        );
        return !inOrderRefs && !inProductRefs && atLeastOneReceived;
      })
      .sort((s1, s2) => s1.reference.localeCompare(s2.reference));
    setSelectableSuborders(newSelectable);
  }, [
    orderRefFields,
    productRefListFields,
    reception.orders,
    reception.productQuantity,
    setSelectableSuborders,
  ]);

  const useHandleOrderReference = async (
    OrderReferenceParams: ExportOrderReferenceType,
    isReception: boolean
  ) => {
    const { referenceType, productRefList } = OrderReferenceParams;
    const suborders = [];
    const refs = [];
    const name = reception.reference;
    const order = [
      {
        reference: reception.reference,
        receptionDate: reception.receptionDate,
        company: reception.company,
      },
    ];
    const type = isReception ? ExportTypes.RECEPTION : ExportTypes.REFCREATION;
    if (
      referenceType === ReferencingType.ALARECEPTION &&
      "receptionProduct" in productRefList
    ) {
      if (isReception) {
        suborders.push({
          article: productRefList.receptionProduct.reference,
          designation: productRefList.receptionProduct.designation,
          quantity: productRefList.receptionProduct.quantity,
        });
      } else {
        refs.push({
          reference:
            type === ExportTypes.REFCREATION
              ? productRefList.receptionProduct.reference
              : productRefList.productReference,
          designation: productRefList.receptionProduct.designation,
        });
      }
    } else if ("productReferences" in productRefList) {
      const transformedData = productRefList.productReferences.map(
        (productRef: any, index: number) => {
          const article = productRef.productReference;
          const refToSearch = productRef.originalProductReference;

          // Find all matching suborderProducts
          const matchingSuborderProducts = productRefList.suborders
            .map((suborder) =>
              suborder.suborderProducts.find(
                (suborderProduct) => suborderProduct.reference === refToSearch
              )
            )
            .filter((p) => p !== undefined);

          // Safely extract designation
          const designation = matchingSuborderProducts[0]?.name || "";

          // Safely get quantity if reception mode is active

          // const quantity = isReception
          //   ? reception.productQuantity.find(
          //       (product) =>
          //         product.productId ===
          //           matchingSuborderProducts[0]?.reference &&
          //         productRef.suborderReferences.find(
          //           (suborderRef) => suborderRef === product.suborderReference
          //         )
          //     )?.receivedQuantity
          //   : undefined;

          const quantity = isReception
            ? getProductQuantityData(
                productRef,
                reception.productQuantity,
                index
              ).foundPQ?.receivedQuantity
            : undefined;
          return {
            article,
            designation,
            quantity,
          };
        }
      );

      if (isReception) {
        // Check for existing articles and add quantities instead of pushing duplicates
        transformedData.forEach((item) => {
          const existingIndex = suborders.findIndex(
            (suborder) => suborder.article === item.article
          );
          if (existingIndex !== -1) {
            // Article exists, add quantities
            const existingQuantity = suborders[existingIndex].quantity || 0;
            const newQuantity = item.quantity || 0;
            suborders[existingIndex].quantity = existingQuantity + newQuantity;
          } else {
            // Article doesn't exist, push new item
            suborders.push(item);
          }
        });
      } else {
        transformedData.forEach((item) => {
          const existingIndex = refs.findIndex(
            (suborder) => suborder.article === item.article
          );
          if (existingIndex !== -1) {
            // Article exists, add quantities
            const existingQuantity = refs[existingIndex].quantity || 0;
            const newQuantity = item.quantity || 0;
            refs[existingIndex].quantity = existingQuantity + newQuantity;
          } else {
            // Article doesn't exist, push new item
            refs.push(item);
          }
        });
      }
    }
    const body = isReception
      ? { type, order, referenceType, suborders }
      : { referenceType, refs, name };
    const response = await exportData(
      token,
      body,
      isReception
        ? "/export/receptionReferencementStockIt"
        : "/export/createReferencementStockIt"
    );
    const filename = response.headers
      .get("Content-Disposition")
      .split("filename=")[1];
    downloadFile(response, filename, "xlsx");
  };

  const handleCancelUpdate = () => {
    resetEditedBlocks();
    setPendingDeletions([]);
    setOpenCancelModal(false);
    togglePopin(false);
  };

  // if value is empty, after creation
  const useRefreshRefItem = (orderRefItem: any) => {
    if (
      !orderRefItem.receptionProduct.reference &&
      orderRefItem.productReference
    ) {
      const filters = {
        reference: orderRefItem.productReference,
        company: reception.company,
      };
      const product = queryClient.getQueryData([
        "getProductByReference",
        filters,
      ]);
      if (product) {
        const newReceptionProduct = {
          reference: orderRefItem.productReference,
          description: product[0]?.description,
          designation: product[0]?.designation,
          quantity: product[0]?.quantity,
          unit: product[0]?.unit,
          categories: product[0]?.categories,
        };
        return {
          ...orderRefItem,
          receptionProduct: newReceptionProduct,
        };
      }
    }
    return orderRefItem;
  };

  const useHandleOnSubmit = async (data: FieldValues, closePopin: boolean) => {
    setIsSavingProducts(true);
    const { orderReferences, productReferenceLists: allProductReferenceLists } =
      data;

    const productReferenceLists = allProductReferenceLists.map((prl) =>
      // Remove empty productReference => must not be referenced
      ({
        ...prl,
        productReferences: prl.productReferences.filter(
          (pr) => !!pr.productReference
        ),
      })
    );

    const productReferences = productReferenceLists.flatMap(
      (prl) => prl.productReferences
    );

    const handleOrderRefProducts = async () => {
      const updatedProducts: any[] = [];
      try {
        // Create / update products for order references
        await processOrderRefProducts(
          orderReferences.map((orderRef) => {
            const receptionProductReference =
              orderRef.receptionProduct?.reference;
            // Regression guard: the editable field is `receptionProduct.reference` but
            // processOrderRefProducts groups on `productReference`. Keep them in sync.
            const productReference =
              orderRef.productReference || receptionProductReference;

            // Quantity is per reception reference (order API), not on the shared product.
            // Do not persist it on the product record.
            const { quantity: _qty, ...receptionProductWithoutQty } =
              orderRef.receptionProduct || {};
            return {
              ...orderRef,
              productReference,
              receptionProduct: {
                ...receptionProductWithoutQty,
                reference: receptionProductReference || productReference,
              },
            };
          }),
          reception.company,
          (index: number, product: any) => {
            updatedProducts.push({ index, product });
          }
        );

        return updatedProducts;
      } catch (err) {
        console.error(
          "error while adding/editing a product for order referencing =>",
          err
        );
      }
    };

    const handleProductRefProducts = async () => {
      try {
        // Create / update products for product references
        await processProductRefProducts(
          productReferenceLists,
          (index: number, prlIndex: number) => {
            form.clearErrors(
              `productReferenceLists.${prlIndex}.productReferences.${index}`
            );
          }
        );
      } catch (err) {
        console.error(
          "Error while adding/editing a product for product referencing =>",
          err
        );
      }
    };

    const handleDeleteProducts = async () => {
      try {
        const filteredPendingDeletions = pendingDeletions.filter(
          (ref) => !toKeepIdenticalReferences.includes(ref)
        );
        if (filteredPendingDeletions.length > 0) {
          const toDeleteProducts = await Promise.all(
            filteredPendingDeletions.map((ref) =>
              ProductService.getProductByReference({ reference: ref })
            )
          );

          const successDeletes = [];
          const errorDeletes = [];
          await Promise.all(
            toDeleteProducts.flat().map(async (product) => {
              const { _id, reference } = product;
              const deleteProductRes = await deleteProduct(token, _id);
              if (deleteProductRes.status !== 200) {
                errorDeletes.push(reference);
              } else {
                successDeletes.push(reference);
              }
            })
          );

          if (errorDeletes.length > 0) {
            openSnackbarError(
              errorDeletes.length > 1
                ? `Les produits ${errorDeletes.join(
                    ", "
                  )} n'ont pas pu être supprimés.`
                : `Le produit ${errorDeletes[0]} n'a pas pu être supprimé.`
            );
          } else if (successDeletes.length > 0) {
            openSnackbar(
              successDeletes.length > 1
                ? `Les produits ${successDeletes.join(
                    ", "
                  )} ont bien été supprimés.`
                : `Le produit ${successDeletes[0]} a bien été supprimé.`
            );
          }
        }
      } catch (err) {
        console.error("Error while deleting products =>", err);
      }
    };

    try {
      const updatedProducts = (await handleOrderRefProducts()) || [];
      await handleProductRefProducts();
      await handleDeleteProducts();

      const productIdByOrderRefIndex = new Map<number, string>();
      updatedProducts.forEach(({ index, product }) => {
        if (typeof index === "number" && product?._id) {
          productIdByOrderRefIndex.set(index, product._id);
        }
      });

      // const newReceptionReferences = [
      //   ...orderReferences.map((orderReference) => {
      //     const referenceData: any = {
      //       orders: orderReference.orders.map((o) => o.id),
      //       originalProductReference: orderReference.originalProductReference,
      //       productReference:
      //         orderReference.productReference ||
      //         orderReference.receptionProduct?.reference,
      //       referenceType: orderReference.referenceType,
      //       suborderReferences: orderReference.suborders.map(
      //         (s) => s.reference
      //       ),
      //     };

      //     // Only include ID if it's an existing reference AND belongs to this reception
      //     if (
      //       orderReference?.id &&
      //       reception.receptionReferences?.some(
      //         (rr) => rr.id === orderReference.id
      //       )
      //     ) {
      //       referenceData.id = orderReference.id;
      //     }

      //     return referenceData;
      //   }),
      //   ...productReferences.map((productReference) => {
      //     const referenceData: any = {
      //       orders: productReference.orders.map((o) => o.id),
      //       originalProductReference: productReference.originalProductReference,
      //       productReference: productReference.productReference,
      //       referenceType: productReference.referenceType,
      //       suborderReferences: productReference.suborders.map(
      //         (s) => s.reference
      //       ),
      //     };

      //     // Only include ID if it's an existing reference AND belongs to this reception
      //     if (
      //       productReference?.id &&
      //       reception.receptionReferences?.some(
      //         (rr) => rr.id === productReference.id
      //       )
      //     ) {
      //       referenceData.id = productReference.id;
      //     }

      //     return referenceData;
      //   }),
      // ];

      // Determine if this is a create or update operation
      // const isCreateOperation = !reception.id;

      // let submitData;

      // if (isCreateOperation) {
      //   // For CREATE: send only the receptionReferences (the backend will handle the rest)
      //   submitData = {
      //     receptionReferences: newReceptionReferences,
      //   };
      // } else {
      //   // console.log(
      //   //   "reception.receptionReferences",
      //   //   reception.receptionReferences
      //   // );
      //   // console.log("pendingDeletions", pendingDeletions);
      //   const filteredExistingReferences = (
      //     reception.receptionReferences || []
      //   ).filter((rr) => {
      //     const shouldKeep = !pendingDeletions.includes(rr.productReference);
      //     return shouldKeep;
      //   });
      //   // console.log("filteredExistingReferences", filteredExistingReferences);
      //   // console.log("newReceptionReferences", newReceptionReferences);
      //   const finalReceptionReferences = [
      //     ...filteredExistingReferences,
      //     ...newReceptionReferences,
      //   ];
      //   // console.log("finalReceptionReferences", finalReceptionReferences);
      //   // For UPDATE: send complete reception data with updated receptionReferences
      //   submitData = {
      //     id: reception.id,
      //     // Include all the original reception data
      //     ...reception,
      //     // Override with updated receptionReferences
      //     receptionReferences: finalReceptionReferences,
      //   };
      // }
      await onSubmit({
        id: reception.id,
        receptionReferences: [
          ...orderReferences.map((orderReference, index) => ({
            // id: orderReference?.id,
            orders: normalizeOrders(orderReference.orders).map((o) => o.id),
            companyId: resolveReferenceCompanyId(
              orderReference.orders,
              reception.company
            ),
            originalProductReference: orderReference.originalProductReference,
            // Send the product reference stored for order referencing
            productReference:
              orderReference.productReference ||
              orderReference.receptionProduct?.reference,
            productId: productIdByOrderRefIndex.get(index),
            referenceType: orderReference.referenceType,
            quantity:
              parseInt(
                orderReference.receptionProduct?.quantity?.toString(),
                10
              ) || 0,
            suborderReferences: orderReference.suborders.map(
              (s) => s.reference
            ),
          })),
          ...(await Promise.all(
            productReferences.map(async (productReference) => {
              const productId = await getCoreProductIdByReference(
                queryClient,
                reception.company,
                productReference.productReference
              );
              return {
                // id: productReference?.id,
                orders: normalizeOrders(productReference.orders).map(
                  (o) => o.id
                ),
                companyId: resolveReferenceCompanyId(
                  productReference.orders,
                  reception.company
                ),
                originalProductReference:
                  productReference.originalProductReference,
                productReference: productReference.productReference,
                referenceType: productReference.referenceType,
                productId,
                quantity: computeReceptionReferenceQuantityFromReception(
                  productReference,
                  reception.productQuantity
                ),
                suborderReferences: productReference.suborders.map(
                  (s) => s.reference
                ),
              };
            })
          )),
        ],
      });

      if (
        // !isCreateOperation &&
        indexToDelete !== null &&
        data.receptionReferences.length > 0 &&
        productReferenceLists.length > 0
      ) {
        // IMPORTANT: Only delete lost references if we're actually in a deletion context
        // Check if we're actually deleting something (not just creating/modifying)
        const isActuallyDeleting =
          toDeleteOrderRef !== null ||
          toDeleteProductRefList !== null ||
          pendingDeletions.length > 0;

        if (isActuallyDeleting) {
          setToDeleteReferences([
            data.receptionReferences[indexToDelete].productReference,
          ]);
        } else {
          // Reset indexToDelete to prevent future unwanted deletions
          setIndexToDelete(null);
        }
      }

      queryClient.removeQueries([
        CacheInvalidationAction.GET_PRODUCT_BY_REFERENCE,
      ]);

      queryClient.removeQueries([
        CacheInvalidationAction.GET_RECEPTION_BY_ID,
        reception.id,
      ]);

      await queryClient.refetchQueries([
        CacheInvalidationAction.GET_RECEPTION_BY_ID,
        reception.id,
      ]);

      socket.emit("invalidateQueries", [
        CacheInvalidationAction.GET_RECEPTION,
        [CacheInvalidationAction.GET_RECEPTION_BY_ID, reception.id],
        CacheInvalidationAction.GET_PRODUCT_STOCK,
        [CacheInvalidationAction.GET_PRODUCT_STOCK, reception.company],
        CacheInvalidationAction.GET_PRODUCT_BY_REFERENCE,
      ]);

      resetEditedBlocks();
      setPendingDeletions([]);

      setToDeleteReferences([]);
      setToKeepReferences([]);
      // setToKeepIdenticalReferences?.([]);
      setToDeleteOrderRef(null);
      setToDeleteProductRefList(null);
      setConfirmedDeletions(false);

      if (closePopin) {
        togglePopin(false);
      } else {
        openSnackbar("Les modifications ont été enregistrées avec succès");
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log("error while submitting referencing form =>", err);
      openSnackbarError("Une erreur est survenue lors de l'enregistrement");
    } finally {
      setIsSavingProducts(false);
    }
  };

  const onDeleteProducts = async () => {
    if (toDeleteOrderRef) {
      addToEditedBlocks({
        id: String(
          toDeleteOrderRef.suborderReferences[0] || toDeleteOrderRef.id
        ),
        type: ReferenceType.ORDER,
      });

      deletePreviousOrderRef(toDeleteOrderRef);
      deletePreviousProductListRef(
        getPreviousProductListFromOrderRef(toDeleteOrderRef)
      );
      const index = orderRefFields.findIndex(
        (orderRef) => orderRef.id === toDeleteOrderRef.id
      );
      removeOrderRef(index);
    }

    if (toDeleteProductRefList) {
      addToEditedBlocks({
        id: String(
          toDeleteProductRefList.suborders[0]?.reference ||
            toDeleteProductRefList.id
        ),
        type: ReferenceType.PRODUCT,
      });

      deletePreviousProductListRef(toDeleteProductRefList);
      const index = productRefListFields.findIndex(
        (productRefList) => productRefList.id === toDeleteProductRefList.id
      );
      removeProductRefList(index);
    }

    // Fix the type error by properly typing the prev parameter
    const newPendingDeletions = [...pendingDeletions, ...toDeleteReferences];
    setPendingDeletions(newPendingDeletions);
    setConfirmedDeletions(true);
    setToDeleteReferences([]);
    setToKeepReferences([]);
    // setToKeepIdenticalReferences([]);
    setToDeleteOrderRef(null);
    setToDeleteProductRefList(null);
    setOpenDeleteModal(false);
  };

  return {
    form,
    handleOnSubmit: useHandleOnSubmit,
    handleOrderReference: useHandleOrderReference,
    handleCancelUpdate,
    refreshRefItem: useRefreshRefItem,
    orderRefFields,
    productRefListFields,
    isLoading: isLoadingDefaultValues,
    selectableOrders,
    onSelectSuborders,
    initialReceptionReferences: reception.receptionReferences,
    removeOrderRef,
    prependOrderRef,
    removeProductRefList,
    prependProductRefList,
    onDeleteProducts,
  };
};
